import * as THREE from 'three';

/**
 * BlendshapeManager - Gestionnaire des blendshapes pour les modèles 3D
 * Permet de contrôler et animer les blendshapes (morph targets) des modèles GLTF
 */
export class BlendshapeManager {
    constructor(model, config = {}) {
        this.model = model;
        this.config = config;
        
        // Stockage des meshes avec blendshapes
        this.blendshapeMeshes = [];
        
        // Mapping des noms de blendshapes
        this.blendshapeMap = new Map();
        
        // Valeurs actuelles des blendshapes (0-1)
        this.currentValues = new Map();
        
        // Valeurs cibles pour l'interpolation
        this.targetValues = new Map();
        
        // Vitesse d'interpolation (0-1, plus proche de 1 = plus rapide)
        this.interpolationSpeed = config.interpolationSpeed || 0.1;
        
        // Seuil minimum pour considérer qu'une valeur a changé
        this.threshold = config.threshold || 0.001;
        
        // Initialiser le manager
        this.initialize();
    }

    /**
     * Initialise le manager en scannant le modèle pour trouver les blendshapes
     */
    initialize() {
        if (!this.model) {
            //console.warn('[BlendshapeManager] Aucun modèle fourni');
            return;
        }

        this.scanForBlendshapes(this.model);
        this.createBlendshapeMap();
        
       //console.log(`[BlendshapeManager] Initialisé avec ${this.blendshapeMeshes.length} mesh(es) contenant des blendshapes`);
       //console.log(`[BlendshapeManager] ${this.blendshapeMap.size} blendshape(s) disponible(s):`, Array.from(this.blendshapeMap.keys()));
    }

    /**
     * Scanne récursivement le modèle pour trouver les meshes avec morphTargets
     */
    scanForBlendshapes(object) {
        if (object.isMesh && object.morphTargetInfluences && object.morphTargetInfluences.length > 0) {
            this.blendshapeMeshes.push(object);
            
            // Initialiser les valeurs actuelles et cibles à 0
            if (object.morphTargetDictionary) {
                Object.keys(object.morphTargetDictionary).forEach(name => {
                    const index = object.morphTargetDictionary[name];
                    this.currentValues.set(`${object.uuid}_${name}`, 0);
                    this.targetValues.set(`${object.uuid}_${name}`, 0);
                });
            }
        }

        // Continuer la recherche dans les enfants
        object.children.forEach(child => {
            this.scanForBlendshapes(child);
        });
    }

    /**
     * Crée une map globale des blendshapes disponibles
     */
    createBlendshapeMap() {
        this.blendshapeMeshes.forEach(mesh => {
            if (mesh.morphTargetDictionary) {
                Object.keys(mesh.morphTargetDictionary).forEach(name => {
                    const index = mesh.morphTargetDictionary[name];
                    
                    if (!this.blendshapeMap.has(name)) {
                        this.blendshapeMap.set(name, []);
                    }
                    
                    this.blendshapeMap.get(name).push({
                        mesh: mesh,
                        index: index,
                        uuid: mesh.uuid
                    });
                });
            }
        });
    }

    /**
     * Définit la valeur d'un blendshape spécifique
     * @param {string} name - Nom du blendshape
     * @param {number} value - Valeur entre 0 et 1
     * @param {boolean} immediate - Si true, applique immédiatement sans interpolation
     */
    setBlendshape(name, value, immediate = false) {
        if (!this.blendshapeMap.has(name)) {
            //console.warn(`[BlendshapeManager] Blendshape "${name}" non trouvé`);
            return;
        }
    
        // Clamp la valeur entre 0 et 1
        value = Math.max(0, Math.min(1, value));

        const blendshapeData = this.blendshapeMap.get(name);
        
        blendshapeData.forEach(data => {
            const key = `${data.uuid}_${name}`;
            this.targetValues.set(key, value);
           // console.log(key, value);
            if (immediate) {
                this.currentValues.set(key, value);
                data.mesh.morphTargetInfluences[data.index] = value;
            }
        });
    }

    /**
     * Récupère la valeur actuelle d'un blendshape
     * @param {string} name - Nom du blendshape
     * @returns {number|null} Valeur actuelle ou null si non trouvé
     */
    getBlendshape(name) {
        if (!this.blendshapeMap.has(name)) {
            return null;
        }

        const blendshapeData = this.blendshapeMap.get(name)[0]; // Prendre le premier
        const key = `${blendshapeData.uuid}_${name}`;
        return this.currentValues.get(key) || 0;
    }

    /**
     * Définit plusieurs blendshapes en une fois
     * @param {Object} values - Objet avec les noms et valeurs des blendshapes
     * @param {boolean} immediate - Si true, applique immédiatement sans interpolation
     */
    setBlendshapes(values, immediate = false) {
        Object.entries(values).forEach(([name, value]) => {
            this.setBlendshape(name, value, immediate);
        });
    }

    /**
     * Remet tous les blendshapes à zéro
     * @param {boolean} immediate - Si true, applique immédiatement sans interpolation
     */
    resetAll(immediate = false) {
        this.blendshapeMap.forEach((_, name) => {
            this.setBlendshape(name, 0, immediate);
        });
    }

    /**
     * Met à jour l'interpolation des blendshapes (à appeler dans la boucle de rendu)
     */
    update() {
        this.currentValues.forEach((currentValue, key) => {
            const targetValue = this.targetValues.get(key);
            
            if (Math.abs(currentValue - targetValue) > this.threshold) {
                // Interpolation linéaire
                const newValue = THREE.MathUtils.lerp(currentValue, targetValue, this.interpolationSpeed);
                this.currentValues.set(key, newValue);
                
                // Appliquer la valeur au mesh correspondant
                this.applyValueToMesh(key, newValue);
            }
        });
    }

    /**
     * Applique une valeur à un mesh spécifique
     * @private
     */
    applyValueToMesh(key, value) {
        const [uuid, name] = key.split('_');
        const blendshapeData = this.blendshapeMap.get(name);
        
        if (blendshapeData) {
            const meshData = blendshapeData.find(data => data.uuid === uuid);
            if (meshData) {
                meshData.mesh.morphTargetInfluences[meshData.index] = value;
            }
        }
    }

    /**
     * Retourne la liste de tous les blendshapes disponibles
     * @returns {Array<string>} Liste des noms de blendshapes
     */
    getAvailableBlendshapes() {
        return Array.from(this.blendshapeMap.keys());
    }

    /**
     * Retourne les informations détaillées sur un blendshape
     * @param {string} name - Nom du blendshape
     * @returns {Object|null} Informations ou null si non trouvé
     */
    getBlendshapeInfo(name) {
        if (!this.blendshapeMap.has(name)) {
            return null;
        }

        const data = this.blendshapeMap.get(name);
        return {
            name: name,
            meshCount: data.length,
            currentValue: this.getBlendshape(name),
            targetValue: this.targetValues.get(`${data[0].uuid}_${name}`),
            meshes: data.map(d => ({
                meshName: d.mesh.name,
                index: d.index,
                uuid: d.uuid
            }))
        };
    }

    /**
     * Définit la vitesse d'interpolation
     * @param {number} speed - Vitesse entre 0 et 1
     */
    setInterpolationSpeed(speed) {
        this.interpolationSpeed = Math.max(0, Math.min(1, speed));
    }

    /**
     * Active/désactive l'interpolation pour un passage immédiat aux valeurs cibles
     * @param {boolean} immediate - Si true, tous les changements seront immédiats
     */
    setImmediateMode(immediate) {
        if (immediate) {
            // Appliquer immédiatement toutes les valeurs cibles
            this.targetValues.forEach((targetValue, key) => {
                this.currentValues.set(key, targetValue);
                this.applyValueToMesh(key, targetValue);
            });
        }
        this.interpolationSpeed = immediate ? 1 : (this.config.interpolationSpeed || 0.1);
    }

    /**
     * Nettoie les ressources du manager
     */
    dispose() {
        this.blendshapeMeshes = [];
        this.blendshapeMap.clear();
        this.currentValues.clear();
        this.targetValues.clear();
       //console.log('[BlendshapeManager] Ressources nettoyées');
    }
}

export default BlendshapeManager;
